import { currentUserIsNurse, currentUserIsDoctor } from '@/globalHelpers/userRole';

/**
 * Removes all non-digit characters from a string
 * @param {string} value - The input string
 * @returns {string} - String with only digits
 */
export const removeNonDigits = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

/**
 * Converts MM/YYYY string format to Date object
 * @param {string} dateString - Date string in MM/YYYY format
 * @returns {Date|null} - Date object or null if invalid
 */
export const parseMedicareExpiryDate = (dateString) => {
  if (!dateString) return null;
  
  const [month, year] = dateString.split('/');
  if (month && year) {
    // Create date with first day of month (Datepicker expects Date object)
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  }
  return null;
};

/**
 * Converts Date object to MM/YYYY string format
 * @param {Date} date - Date object
 * @returns {string} - Date string in MM/YYYY format
 */
export const formatMedicareExpiryDate = (date) => {
  if (!date) return '';
  
  const month = (date.getMonth() + 1 < 10 ? '0' : '') + (date.getMonth() + 1);
  const year = date.getFullYear();
  return `${month}/${year}`;
};

/**
 * Validates Medicare Card Number (exactly 10 digits)
 * @param {string} medicareNumber - Medicare number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateMedicareNumber = (medicareNumber) => {
  if (!medicareNumber) return true; // Optional field
  return /^\d{10}$/.test(medicareNumber);
};

/**
 * Validates Individual Reference Number (IRN) - single digit 1-9
 * @param {string} irn - IRN to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateIRN = (irn) => {
  if (!irn) return true; // Optional field
  return /^[1-9]$/.test(irn);
};

/**
 * Validates Individual Healthcare Number (IHI) - exactly 16 digits
 * @param {string} ihiNumber - IHI number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateIHINumber = (ihiNumber) => {
  if (!ihiNumber) return true; // Optional field
  return /^\d{16}$/.test(ihiNumber);
};

/**
 * Validates Medicare expiry date - must be in the future
 * @param {string} expiryDate - Date string in MM/YYYY format
 * @returns {boolean} - True if valid (future date), false otherwise
 */
export const validateMedicareExpiryDate = (expiryDate) => {
  if (!expiryDate) return true; // Optional field
  
  const [month, year] = expiryDate.split('/');
  if (!month || !year) return false;
  
  const expiry = new Date(parseInt(year), parseInt(month) - 1, 1);
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  return expiry > currentMonth;
};

export const formatIHINumber = (ihiNumber) => {
  if (!ihiNumber) return null;
    
  // Remove all spaces first, then format consistently
  const cleanNumber = ihiNumber.replace(/\s/g, '');
  return cleanNumber.replace(/(.{4})/g, '$1 ').trim();
};

export const formatMedicareNumber = (medicareNumber) => {
  if (!medicareNumber) return null;
  return medicareNumber.replace(/(\d{4})(\d{5})(\d{1})/, '$1 $2 $3');
};

export function hasHealthTreatment(medicareInfo, ihiNumber) {
  const isNurseOrDoctor = currentUserIsNurse() || currentUserIsDoctor();

  const hasMedicareDetails =
      (medicareInfo && medicareInfo.medicareCardName) ||
      (medicareInfo && medicareInfo.medicareNumber) ||
      (medicareInfo && medicareInfo.medicareRefNumber) ||
      (medicareInfo && medicareInfo.medicareExpiryDate) ||
      (ihiNumber);
    
  return isNurseOrDoctor && hasMedicareDetails;
}